import {
  Button,
  Checkbox,
  Field,
  FormLabel,
  Input,
  Label,
  PlusIcon,
  Radio,
  RadioGroup,
  Select,
} from '@quickmd-llc/design-system'
import { useEffect, useRef, useState } from 'react'
import type { ChangeEvent, MutableRefObject } from 'react'
import { useFieldArray, Controller } from 'react-hook-form'
import type {
  UseFormRegister,
  Control,
  FieldErrors,
  UseFormWatch,
  UseFormSetValue,
  UseFormClearErrors,
  UseFormTrigger,
} from 'react-hook-form'
import type { MedicationFormValues } from '../../types'
import { UNIT_OPTIONS } from '../../constants'
import { useMedicationCategories } from '../../hooks/useMedicationCategories'
import { PricingTierRow } from './PricingTierRow'
import { FormSectionCard } from './FormSectionCard'

interface DrugClassificationSectionProps {
  register: UseFormRegister<MedicationFormValues>
  control: Control<MedicationFormValues>
  errors: FieldErrors<MedicationFormValues>
  watch: UseFormWatch<MedicationFormValues>
  setValue: UseFormSetValue<MedicationFormValues>
  clearErrors: UseFormClearErrors<MedicationFormValues>
  trigger: UseFormTrigger<MedicationFormValues>
  showIndicator: boolean
  isValid: boolean
  touchedFields: Partial<Record<keyof MedicationFormValues, unknown>>
  isSubmitted: boolean
  formatNdc: (raw: string) => string
}

export function DrugClassificationSection({
  register,
  control,
  errors,
  watch,
  setValue,
  clearErrors,
  trigger,
  showIndicator,
  isValid,
  touchedFields,
  isSubmitted,
  formatNdc,
}: DrugClassificationSectionProps) {
  const { fields, append, remove } = useFieldArray({ control, name: 'pricingTiers' })
  const categories = useMedicationCategories()

  const gcnEnabled = watch('gcnEnabled')
  const ndcEnabled = watch('ndcEnabled')
  const otherEnabled = watch('otherEnabled')

  const [showOther, setShowOther] = useState(otherEnabled)
  const otherInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (showOther) {
      otherInputRef.current?.focus()
    }
  }, [showOther])

  function handleDrugCodeToggle(name: 'gcnEnabled' | 'ndcEnabled' | 'otherEnabled', checked: boolean) {
    setValue(name, checked, { shouldTouch: true })
    if (!checked) {
      const codeField =
        name === 'gcnEnabled' ? 'gcn'
        : name === 'ndcEnabled' ? 'ndc'
        : 'otherDrugCode'
      clearErrors(codeField)
      clearErrors('gcnEnabled')
      if (name === 'otherEnabled') {
        setValue('otherDrugCode', '')
        setShowOther(false)
      }
    }
    trigger('gcnEnabled')
  }

  function handleAddOther() {
    setShowOther(true)
    setValue('otherEnabled', true, { shouldTouch: true })
    trigger('gcnEnabled')
  }

  const showAtLeastOneError = (touchedFields.gcnEnabled || isSubmitted) && errors.gcnEnabled
  const showGcnError = (touchedFields.gcn || isSubmitted) && errors.gcn
  const showNdcError = (touchedFields.ndc || isSubmitted) && errors.ndc
  const showOtherError = (touchedFields.otherDrugCode || isSubmitted) && errors.otherDrugCode
  const { ref: otherRegisterRef, ...otherRegisterRest } = register('otherDrugCode')

  const showCategoryError = (touchedFields.category || isSubmitted) && errors.category

  return (
    <FormSectionCard title='Drug classification' showIndicator={showIndicator} isValid={isValid}>
      <div className='flex flex-col gap-sp-3'>
        {/* Category — first */}
        <div className='grid grid-cols-2 gap-sp-3'>
          <div>
            <Label size='lg' className='mb-sp-1.5 block'>
              Category
            </Label>
            <Select {...register('category')} error={Boolean(showCategoryError)}>
              <option value=''>Select</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
            {showCategoryError ?
              <Label size='sm' className='mt-sp-0.5 text-text-error'>
                {errors.category?.message}
              </Label>
            : null}
          </div>
          <span />
        </div>

        {/* Panel divider */}
        <hr className='-mx-sp-3 border-0 border-t border-border-medium' />

        {/* Drug code — second */}
        <div>
          <Label size='lg' className='mb-sp-1.5 block'>
            Drug code
          </Label>
          {showAtLeastOneError ?
            <Label size='sm' className='mb-sp-1 text-text-error'>
              {errors.gcnEnabled?.message}
            </Label>
          : null}
          {/* 2-column wrapping grid: GCN | NDC in row 1, Other | (empty) in row 2 */}
          <div className='grid grid-cols-2 gap-sp-3'>
            {/* GCN */}
            <div className='flex flex-col gap-sp-2'>
              <Field className='flex items-center gap-sp-1'>
                <Checkbox
                  checked={gcnEnabled}
                  onChange={(checked: boolean) => handleDrugCodeToggle('gcnEnabled', checked)}
                />
                <FormLabel size='md'>GCN</FormLabel>
              </Field>
              <Controller
                control={control}
                name='gcn'
                render={({ field }) => (
                  <Input
                    value={field.value}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      field.onChange(e.target.value.replace(/\D/g, '').slice(0, 6))
                    }}
                    onBlur={field.onBlur}
                    placeholder='00000'
                    inputMode='numeric'
                    disabled={!gcnEnabled}
                    error={Boolean(showGcnError)}
                  />
                )}
              />
              {showGcnError ?
                <Label size='sm' className='text-text-error'>
                  {errors.gcn?.message}
                </Label>
              : null}
            </div>

            {/* NDC */}
            <div className='flex flex-col gap-sp-2'>
              <Field className='flex items-center gap-sp-1'>
                <Checkbox
                  checked={ndcEnabled}
                  onChange={(checked: boolean) => handleDrugCodeToggle('ndcEnabled', checked)}
                />
                <FormLabel size='md'>NDC</FormLabel>
              </Field>
              <Controller
                control={control}
                name='ndc'
                render={({ field }) => (
                  <Input
                    value={formatNdc(field.value)}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      const raw = e.target.value.replace(/\D/g, '').slice(0, 11)
                      field.onChange(raw)
                    }}
                    onBlur={field.onBlur}
                    placeholder='00000-0000-00'
                    inputMode='numeric'
                    disabled={!ndcEnabled}
                    error={Boolean(showNdcError)}
                  />
                )}
              />
              {showNdcError ?
                <Label size='sm' className='text-text-error'>
                  {errors.ndc?.message}
                </Label>
              : null}
            </div>

            {/* Other — shown when added, otherwise show "Add other" button */}
            {showOther ?
              <div className='flex flex-col gap-sp-2'>
                <Field className='flex items-center gap-sp-1'>
                  <Checkbox
                    checked={otherEnabled}
                    onChange={(checked: boolean) => handleDrugCodeToggle('otherEnabled', checked)}
                  />
                  <FormLabel size='md'>Other</FormLabel>
                </Field>
                <Input
                  {...otherRegisterRest}
                  ref={(el) => {
                    otherRegisterRef(el)
                    ;(otherInputRef as MutableRefObject<HTMLInputElement | null>).current = el
                  }}
                  placeholder='13 characters (alphanumeric)'
                  disabled={!otherEnabled}
                  maxLength={13}
                  error={Boolean(showOtherError)}
                />
                {showOtherError ?
                  <Label size='sm' className='text-text-error'>
                    {errors.otherDrugCode?.message}
                  </Label>
                : null}
              </div>
            : <Button
                type='button'
                priority='tertiary'
                size='s'
                onClick={handleAddOther}
                className='w-fit justify-start'>
                <PlusIcon iconSize='s' />
                Add other
              </Button>
            }
          </div>
        </div>

        {/* Panel divider */}
        <hr className='-mx-sp-3 border-0 border-t border-border-medium' />

        {/* Dispensing unit + Amount and pricing — grid-cols-2 matches drug code section so center line aligns */}
        <div className='grid grid-cols-2 items-start gap-x-sp-3 gap-y-sp-1'>
          {/* Dispensing unit label */}
          <Label size='lg' className='col-span-2 block'>
            Dispensing unit
          </Label>
          {/* Dispensing unit select — col 1 only */}
          <Select {...register('unit')}>
            {UNIT_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </Select>
          <span /> {/* col 2 empty */}
          {/* Amount and pricing label */}
          <Label size='lg' className='col-span-2 mt-sp-2 block'>
            Amount and pricing
          </Label>
          {/* Pricing tier rows — Fragment children are direct grid items */}
          {fields.map((field, index) => (
            <PricingTierRow
              key={field.id}
              index={index}
              control={control}
              errors={errors}
              onRemove={() => remove(index)}
              showRemove={fields.length > 1}
              touchedFields={touchedFields}
              isSubmitted={isSubmitted}
            />
          ))}
          {/* Add button + errors */}
          <div className='col-span-2 mt-sp-1'>
            <Button
              type='button'
              priority='tertiary'
              size='s'
              onClick={() => append({ quantity: 30, patientPrice: 0 })}>
              <PlusIcon iconSize='s' />
              Add
            </Button>
            {!!(isSubmitted || touchedFields.pricingTiers) &&
              !!errors.pricingTiers?.root &&
              typeof errors.pricingTiers.root.message === 'string' && (
                <Label size='sm' className='mt-sp-0.5 text-text-error'>
                  {errors.pricingTiers.root.message}
                </Label>
              )}
          </div>
        </div>

        {/* Controlled substance — third */}
        <div>
          <Label size='lg' className='mb-sp-1.5 block'>
            Is this a controlled substance?
          </Label>
          <Controller
            control={control}
            name='isControlledSubstance'
            render={({ field: { onChange, value } }) => (
              <RadioGroup
                value={value === null ? '' : String(value)}
                onChange={(v: string) => onChange(v === 'true')}
                className='flex flex-col gap-sp-1'>
                <Field className='flex items-center gap-sp-1'>
                  <Radio value='true' />
                  <FormLabel size='md'>Yes</FormLabel>
                </Field>
                <Field className='flex items-center gap-sp-1'>
                  <Radio value='false' />
                  <FormLabel size='md'>No</FormLabel>
                </Field>
              </RadioGroup>
            )}
          />
        </div>
      </div>
    </FormSectionCard>
  )
}
